/**
 * The container messaging channel, as the two apps see it.
 *
 * One thread per container — not per order and not per driver — because a
 * container is what is dispatched, executed and rung about. See
 * `backend/models/ContainerMessage.js` for the reasoning in full.
 */

/** Which end of the channel. Not a role — a side. */
export type ContainerMessageSide = "driver" | "office";

/**
 * The five buttons a driver has instead of a keyboard.
 *
 * Mirrors `CONTAINER_MESSAGE_TEMPLATES` in backend/models/ContainerMessage.js,
 * which is the authority: the server renders the stored text from the key and
 * refuses a key it does not know. Kept in step by hand, exactly as the status
 * and stop-role unions in `containerOrders.ts` are — the alternative is a
 * request on every load of a screen whose whole point is to be fast in a cab.
 */
export const CONTAINER_MESSAGE_TEMPLATE_KEYS = [
  "on_my_way",
  "arrived",
  "problem_at_site",
  "running_late",
  "need_support",
] as const;

export type ContainerMessageTemplateKey =
  (typeof CONTAINER_MESSAGE_TEMPLATE_KEYS)[number];

/** Where the container was when something was said about it. */
export interface ContainerMessageContext {
  lineStatus?: string;
  stopId?: string | null;
  /** What that stop was called at the time — a stop can be removed later. */
  stopLabel?: string;
}

export interface ContainerMessage {
  _id: string;
  companyId: string;
  lineId: string;
  containerOrderId: string;
  orderNumber?: string;
  lineNumber?: string;

  from: ContainerMessageSide;
  kind: "text" | "quick";
  /**
   * Which button, when it was one.
   *
   * Rendered from this in the reader's own language when the app knows the key,
   * so an Estonian driver's "Arrived" reaches an Estonian dispatcher in
   * Estonian. `body` is the fallback for a key shipped after this client was.
   */
  templateKey?: ContainerMessageTemplateKey | null;
  body: string;

  authorId: string;
  /** Snapshotted at send time — a thread has to read after somebody leaves. */
  authorName?: string;
  authorRole?: "driver" | "admin" | "user";

  vehicleId?: string | null;
  deviceId?: string | null;
  context?: ContainerMessageContext;

  /** When the other side saw it. Null until they did — this is the tick. */
  readAt?: string | null;
  readBy?: string | null;

  createdAt: string;
  updatedAt: string;
}

/**
 * The thread as a counter, which is what every *list* actually wants.
 *
 * Carried on the container itself (`ContainerOrderLine.messaging`) so a badge
 * on a list of two hundred rows costs nothing, and pushed on its own over the
 * socket so a screen that is not showing the conversation is never sent the
 * words. See `emitContainerMessageThread` in backend/socket/emitters.js.
 */
export interface ContainerMessaging {
  total?: number;
  /** Waiting on the office, and waiting on the truck. */
  unreadForOffice?: number;
  unreadForDriver?: number;
  lastMessageAt?: string | null;
  lastMessageFrom?: ContainerMessageSide | null;
  lastMessagePreview?: string;
  officeReadAt?: string | null;
  driverReadAt?: string | null;
}

/** What a thread page answers with. */
export interface ContainerMessagePage {
  messages: ContainerMessage[];
  hasMore: boolean;
  /** Opaque cursor for the page above this one. Null when there is none. */
  cursor: string | null;
  messaging: ContainerMessaging;
  pageSize: number;
}

// ── Socket payloads ─────────────────────────────────────────────────────────

/** A message, to whoever has that container open. */
export interface ContainerMessageEvent {
  lineId: string;
  message: ContainerMessage;
}

/** The counters, to the dispatch floor and to the cab. No message body. */
export interface ContainerMessageThreadEvent {
  lineId: string;
  lineNumber?: string;
  orderNumber?: string;
  containerOrderId?: string;
  messaging: ContainerMessaging;
}

/** One side caught up — turns the sender's ticks through. */
export interface ContainerMessageReadEvent {
  lineId: string;
  side: ContainerMessageSide;
  readAt: string;
}
